import { useRef, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { formatTime } from '@brimo/shared';
import { ApiError, api, errorMessage } from '../../lib/api';
import { Banner, Button, Card, Checkbox, Eyebrow, TextField, TextLink } from '../../ds/components';
import { nextRouteFor, useSessionQuery } from '../../lib/session';
import { AuthFrame } from './AuthFrame';
import { lockDetailsOf, routeForNext, useForgotPassword, type LockDetails, type LoginResponse } from './auth';
import styles from './LoginPage.module.css';

interface LocationState {
  from?: string;
  /** Banner shown once, e.g. after the session ended ("Bitte melden Sie sich erneut an."). */
  message?: string;
  tone?: 'info' | 'danger' | 'success';
}

function stateOf(value: unknown): LocationState {
  return typeof value === 'object' && value !== null ? value : {};
}

/** Shell 19: the account is locked after five failures; the reset link lifts the lock (D-01). */
function LockedCard({ lock }: { lock: LockDetails }) {
  const forgot = useForgotPassword();
  const until = `${formatTime(lock.until)} Uhr`;
  const since = `${formatTime(lock.lockedSince)} Uhr`;
  return (
    <Card padded>
      <Eyebrow>Konto gesperrt</Eyebrow>
      <div className={styles.afterEyebrow}>
        <h1 className={styles.title}>Zu viele Fehlversuche.</h1>
        <p className={styles.subtitle}>
          Ihr Konto ist bis {until} gesperrt.{' '}
          {lock.adminName ? `Ihr Admin ${lock.adminName} wurde informiert.` : 'Ihr Admin wurde informiert.'}
        </p>
      </div>
      <dl className={styles.facts}>
        <div className={styles.fact}>
          <dt>Konto</dt>
          <dd>{lock.email}</dd>
        </div>
        <div className={styles.fact}>
          <dt>Gesperrt seit</dt>
          <dd>{since}</dd>
        </div>
        <div className={styles.fact}>
          <dt>Entsperrt um</dt>
          <dd>{until}</dd>
        </div>
      </dl>
      <div className={styles.lockActions}>
        {forgot.isError ? <Banner tone="danger">{errorMessage(forgot.error)}</Banner> : null}
        {forgot.isSuccess ? (
          <Banner tone="success">Der Link ist unterwegs an {lock.email}.</Banner>
        ) : (
          <Button loading={forgot.isPending} onClick={() => forgot.mutate(lock.email)}>
            Passwort zurücksetzen
          </Button>
        )}
        <span className={styles.lockHint}>Der Link kommt an {lock.email} und entsperrt sofort.</span>
      </div>
    </Card>
  );
}

/** Shell 16/18: e-mail + password; Shell 19 on a 423. */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const state = stateOf(location.state);
  const session = useSessionQuery();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lock, setLock] = useState<LockDetails | null>(null);
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState<LocationState | null>(state.message ? state : null);
  const passwordRef = useRef<HTMLInputElement>(null);

  if (session.data && nextRouteFor(session.data) === null && session.data.active !== null) {
    return <Navigate to={state.from ?? '/home'} replace />;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api<LoginResponse>('/auth/login', {
        method: 'POST',
        body: { email, password, remember },
      });
      await queryClient.invalidateQueries({ queryKey: ['session'] });
      await navigate(routeForNext(result.next, state.from ?? '/home'), { replace: true });
    } catch (err) {
      const locked = lockDetailsOf(err);
      if (locked) {
        setLock(locked);
        return;
      }
      setError(err instanceof ApiError ? err.message : errorMessage(err));
      setPassword('');
      passwordRef.current?.focus();
    } finally {
      setPending(false);
    }
  }

  if (lock) {
    return (
      <AuthFrame>
        <LockedCard lock={lock} />
      </AuthFrame>
    );
  }

  return (
    <AuthFrame>
      <Card padded>
        <h1 className={styles.title}>Anmelden</h1>
        <p className={styles.subtitle}>Mit Ihrer geschäftlichen E-Mail-Adresse.</p>
        {notice?.message ? (
          <div className={styles.banner}>
            <Banner tone={notice.tone ?? 'info'}>{notice.message}</Banner>
          </div>
        ) : null}
        {error ? (
          <div className={styles.banner}>
            <Banner tone="danger">{error}</Banner>
          </div>
        ) : null}
        <form className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
          <TextField
            label="E-Mail"
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <TextField
            ref={passwordRef}
            label="Passwort"
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            invalid={Boolean(error) && !password}
          />
          <div className={styles.row}>
            <Checkbox
              label="Angemeldet bleiben"
              checked={remember}
              onChange={(event) => setRemember(event.target.checked)}
            />
            <span className={styles.rowEnd}>
              <TextLink to="/forgot-password">Passwort vergessen?</TextLink>
            </span>
          </div>
          <Button type="submit" block loading={pending}>
            Anmelden
          </Button>
        </form>
      </Card>
    </AuthFrame>
  );
}
